/**
 * 날씨 위젯 (#406) — 필리핀 주요 도시. Open-Meteo(무키) 서버 수집 → Firestore onSnapshot.
 */
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

interface City {
  name: string;
  temp: number;
  code: number;
  max: number;
  min: number;
}
interface WeatherData {
  cities?: City[];
  updated_at?: string;
}

// WMO weather_code → 카테고리(아이콘+i18n 키)
function wcat(code: number): { key: string; icon: string } {
  if (code === 0) return { key: "wxClear", icon: "☀️" };
  if (code <= 3) return { key: "wxCloudy", icon: "⛅" };
  if (code <= 48) return { key: "wxFog", icon: "🌫️" };
  if (code <= 67) return { key: "wxRain", icon: "🌧️" };
  if (code <= 77) return { key: "wxSnow", icon: "❄️" };
  if (code <= 82) return { key: "wxShower", icon: "🌦️" };
  return { key: "wxStorm", icon: "⛈️" };
}

export default function WeatherWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState<WeatherData | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "trippang_widgets", "weather"),
      (snap) => { if (snap.exists()) setData(snap.data() as WeatherData); },
      (err) => console.error("weather widget snapshot:", err)
    );
    return () => unsub();
  }, []);

  return (
    <div className="border-b border-slate-200 px-3 py-2.5">
      <div className="mb-1.5 text-xs font-semibold text-sky-600">{t("widget.weatherTitle")}</div>
      {!data?.cities?.length ? (
        <div className="py-1 text-[11px] text-slate-400">{t("widget.loading")}</div>
      ) : (
        // 도시별 row를 각자 flex로 쌓으면 온도/최고최저/날씨 텍스트 폭이 row마다 달라 세로 정렬이 안 맞음
        // → 전체를 하나의 grid로 묶어 컬럼 폭을 공유(우측 3컬럼 우측정렬)
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-1.5 gap-y-1.5">
          {data.cities.map((c) => {
            const w = wcat(c.code);
            return (
              <Fragment key={c.name}>
                <span className="flex items-center gap-1 text-[11px] text-slate-600">
                  <span>{w.icon}</span>
                  {t(`widget.city_${c.name}`)}
                </span>
                <span className="text-right text-xs font-bold tabular-nums text-slate-900">{Math.round(c.temp)}°</span>
                <span className="text-right text-[10px] tabular-nums text-slate-400">{Math.round(c.max)}°/{Math.round(c.min)}°</span>
                <span className="text-right text-[10px] text-slate-500">{t(`widget.${w.key}`)}</span>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
